'use client';

import NextLink from 'next/link';
import { useLocale } from 'next-intl';
import type { ComponentPropsWithRef, MouseEvent, ReactNode } from 'react';

import { useBookingLink } from '@/features/landing/hooks/useBookingLink';
import { trackingManager } from '@/lib/tracking/tracking-manager-instance';
import type { BookingCtaLocation } from '@/lib/tracking/types/landing-events';

type BookingLinkProps = Omit<ComponentPropsWithRef<typeof NextLink>, 'href' | 'target' | 'rel'> & {
  location: BookingCtaLocation;
  children: ReactNode;
};

/**
 * Every booking CTA renders through this link, so each click sends exactly one
 * LANDING_BOOKING_CLICKED before the caller's own onClick. Other props (className, ref,
 * the onClick merged in by `Button asChild`) pass through to the anchor.
 */
export function BookingLink({ location, onClick, children, ...rest }: BookingLinkProps) {
  const bookingLink = useBookingLink();
  const language = useLocale();

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    trackingManager.trackEvent('LANDING_BOOKING_CLICKED', {
      ctaLocation: location,
      destinationType: bookingLink.href.startsWith('mailto:') ? 'mailto' : 'web',
      language,
    });
    onClick?.(event);
  };

  return (
    <NextLink
      {...rest}
      {...bookingLink}
      data-testid="booking-cta"
      data-booking-location={location}
      onClick={handleClick}
    >
      {children}
    </NextLink>
  );
}
